import { useState, useRef, useEffect, useMemo } from "react";
import { FiMoreHorizontal, FiEye, FiX, FiDownload } from "react-icons/fi";
import SummaryApi from "../common/SummaryApi";
import api from "../common/apiClient";
import * as XLSX from "xlsx";
import PageLoader from "../helpers/PageLoader";

const EnquiryList = () => {
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All Enquiries");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [selectedMessage, setSelectedMessage] = useState(null);

  const btnRefs = useRef({});
  const menuRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch enquiries
  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const res = await api({
        url: SummaryApi.getAllEnquiries.url,
        method: SummaryApi.getAllEnquiries.method,
      });
      const data = res.data || [];
      setEnquiries(data);
    } catch (err) {
      console.error("Fetch enquiries error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const categories = useMemo(() => {
    const unique = [...new Set(enquiries.map((e) => e.interest).filter(Boolean))];
    return unique;
  }, [enquiries]);

  const filteredData = useMemo(() => {
    let data = enquiries;
    if (filter !== "All Enquiries") {
      data = data.filter((item) => item.interest === filter);
    }
    if (search) {
      const term = search.toLowerCase();
      data = data.filter(
        (item) =>
          item.name?.toLowerCase().includes(term) ||
          item.phone?.includes(term) ||
          item.email?.toLowerCase().includes(term)
      );
    }
    return data;
  }, [enquiries, search, filter]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  
  const currentData = useMemo(() => {
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, startIndex]);

  // EXPORT TO EXCEL LOGIC
  const exportToExcel = () => {
    // 1. Prepare the data (Converting numbers/all types to strings using `${}`)
    const excelData = filteredData.map((item) => ({
      "Name": `${item.name || ""}`,
      "Interest": `${item.interest || "-"}`,
      "Phone": `${item.phone || ""}`,
      "Email": `${item.email || ""}`,
      "Message": `${item.message || ""}`,
      "Visitors Count": `${item.visitorsCount || 0}`,
      "Enquiry Date": `${new Date(item.createdAt).toLocaleDateString()}`,
    }));

    // 2. Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // 3. Calculate Column Widths based on word count/length
    const colWidths = Object.keys(excelData[0] || {}).map((key) => {
      const headerLen = key.length;
      const maxCellLen = excelData.reduce((max, row) => {
        const cellLen = row[key] ? row[key].toString().length : 0;
        return Math.max(max, cellLen);
      }, headerLen);
      
      return { wch: maxCellLen + 2 }; // Add a little padding
    });

    worksheet["!cols"] = colWidths;

    // 4. Create Workbook and save
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
    XLSX.writeFile(workbook, "Enquiry_List.xlsx");
  };

  const handleToggle = (id) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      return;
    }
    const button = btnRefs.current[id];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const dropdownHeight = 50; 
    const dropdownWidth = 140;
    
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let topPosition;
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      topPosition = rect.top - dropdownHeight;
    } else {
      topPosition = rect.bottom + 5;
    }

    setMenuPosition({ 
      top: topPosition, 
      left: rect.left - dropdownWidth + rect.width 
    });
    setOpenMenuId(id);
  };

  const viewMessage = (item) => {
    setSelectedMessage(item);
    setOpenMenuId(null);
  };

  useEffect(() => {
    const handleEvents = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !Object.values(btnRefs.current).some((btn) => btn?.contains(event.target))
      ) {
        setOpenMenuId(null);
      }
    };

    const handleScrollResize = () => {
      setOpenMenuId(null);
    };

    document.addEventListener("mousedown", handleEvents);
    window.addEventListener("scroll", handleScrollResize, true); 
    window.addEventListener("resize", handleScrollResize);

    return () => {
      document.removeEventListener("mousedown", handleEvents);
      window.removeEventListener("scroll", handleScrollResize, true);
      window.removeEventListener("resize", handleScrollResize);
    };
  }, []);

  if (loading) {
    return (
        <PageLoader />
    );
  }
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl pb-4 md:text-3xl font-serif text-gray-800">
          Enquiry List
        </h1>
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 bg-white border px-4 py-2 rounded-md text-sm hover:bg-gray-50 transition-all"
        >
          <FiDownload size={16} />
          <span className="lg:block">Export</span>
        </button>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded-md px-4 py-2 text-sm w-full lg:w-80 outline-none focus:ring-2 ring-gray-200"
        />

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border rounded-md px-4 py-2 text-sm outline-none"
          >
            <option>All Enquiries</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <div className="text-sm text-gray-500">
            Showing {filteredData.length} entries
          </div>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-hide border rounded-lg bg-white">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-5 bg-gray-100 px-6 py-4 text-sm font-semibold text-gray-700">
            <div>Name</div>
            <div>Interest</div>
            <div>Contact</div>
            <div>Enquiry Date</div>
            <div className="text-center">Actions</div>
          </div>

          {currentData.length === 0 ? (
            <div className="px-6 py-10 text-center text-gray-500">
              No enquiries found
            </div>
          ) : (
            currentData.map((item) => (
              <div
                key={item._id}
                className="grid grid-cols-5 items-center px-6 py-4 text-sm border-t hover:bg-[#F7F9F4] transition-colors"
              >
                <div className="font-medium text-gray-800">{item.name}</div>
                <div>{item.interest || "-"}</div>
                <div>{item.phone}</div>
                <div>{new Date(item.createdAt).toLocaleDateString()}</div>

                <div className="flex justify-center">
                  <button
                    ref={(el) => (btnRefs.current[item._id] = el)}
                    onClick={() => handleToggle(item._id)}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <FiMoreHorizontal size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {openMenuId && (
          <div
            ref={menuRef}
            className="fixed w-36 bg-white border rounded-lg shadow-xl text-sm z-[9999] overflow-hidden"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            <button
              onClick={() => {
                const item = enquiries.find(e => e._id === openMenuId);
                viewMessage(item);
              }}
              className="flex items-center gap-2 w-full px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              <FiEye className="text-blue-500" /> View Details
            </button>
          </div>
        )}

        {filteredData.length > 0 && (
          <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-t">
            <div className="text-sm text-gray-600 font-medium">
              Page {currentPage} of {totalPages}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-1.5 border rounded-md bg-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-1.5 border rounded-md bg-[#45A301] text-white text-sm font-medium disabled:opacity-50 hover:bg-[#3a8a01] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center border-b px-6 py-4 bg-gray-50">
              <h3 className="font-bold text-gray-800">Enquiry Details</h3>
              <button 
                onClick={() => setSelectedMessage(null)}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="px-6 py-6 space-y-4 text-sm">
              <div className="grid grid-cols-3">
                <b className="text-gray-500 uppercase text-[10px] tracking-wider">Name</b>
                <div className="col-span-2 text-gray-800 font-medium">{selectedMessage.name}</div>
              </div>
              <div className="grid grid-cols-3">
                <b className="text-gray-500 uppercase text-[10px] tracking-wider">Phone</b>
                <div className="col-span-2 text-gray-800 font-medium">{selectedMessage.phone}</div>
              </div>
              <div className="grid grid-cols-3">
                <b className="text-gray-500 uppercase text-[10px] tracking-wider">Interest</b>
                <div className="col-span-2">
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold">
                    {selectedMessage.interest || "General"}
                  </span>
                </div>
              </div>
              <div className="pt-2">
                <b className="text-gray-500 uppercase text-[10px] tracking-wider block mb-2">Message</b>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 italic text-gray-700 leading-relaxed">
                  "{selectedMessage.message || "No message provided."}"
                </div>
              </div>
              <div className="text-[11px] text-gray-400 text-right pt-2">
                Received on: {new Date(selectedMessage.createdAt).toLocaleString()}
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button 
                onClick={() => setSelectedMessage(null)}
                className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryList;